import { useAuth } from '@/app/AuthContext.jsx';
import {
  useActiveDiligencias,
  useCasesList,
  useCourts,
  useDocumentTitle,
  useExpFeatures,
  useTribunals,
} from '@/hooks';
import { STAGE_LABELS, causaDiligenciaStage, titleCase } from '@/lib';
import { extractList, extractTotal } from '@/lib/apiList';
import { StagePill, useToast } from '@/ui';
import { Plus, Upload } from '@/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import CausasLista from './CausasLista';
import SuscribirCausaModal from './SuscribirCausaModal';
import { buildRol, causaVerificationDisplay } from './causaDisplay';
import { COMPETENCE_FILTER_OPTIONS } from './causasFilters';
import { useNuevaCausaForm } from './useNuevaCausaForm.js';

// Filtros multi (FilterDropdown) → query param CSV ("a,b"); single pasa tal cual.
function csv(v) {
  if (Array.isArray(v)) return v.length ? v.join(',') : undefined;
  return v;
}

// Column visibility. Rol + Carátula are always shown; the other three toggle.
// Persisted so the choice survives reloads.
// ponytail: inline localStorage; promote to a shared hook if a 2nd table needs it.
const CAUSAS_COLS = [
  ['tribunal', 'Tribunal'],
  ['diligencia', 'Diligencia activa'],
  ['ult', 'Últ. actuación'],
];
const CAUSAS_COLS_KEY = 'recepthor:causasCols';
const ALL_CAUSAS_COLS = CAUSAS_COLS.map(([k]) => k);
function loadCausasCols() {
  try {
    const v = JSON.parse(localStorage.getItem(CAUSAS_COLS_KEY));
    return Array.isArray(v)
      ? v.filter((k) => ALL_CAUSAS_COLS.includes(k))
      : ALL_CAUSAS_COLS;
  } catch {
    return ALL_CAUSAS_COLS;
  }
}

function tribunalLabel(c) {
  return c.tribunalName || c.tribunal?.name || c.tribunal || '—';
}

// Diligencia set for the server-side "Diligencia" filter. The values are the
// stage vocabulary (tasks.stage) plus the sentinel NONE (causas with no active
// task), and the labels come from the one stage map so the filter and the
// pills say the same word. "Expirada" is DERIVED from the deadline, not a
// persisted stage, so it is not offerable server-side.
const DILIGENCIA_FILTER_OPTIONS = [
  { value: 'NONE', label: 'Sin diligencia' },
  ...Object.entries(STAGE_LABELS).map(([value, label]) => ({ value, label })),
];

// Map a /cases row → the 5 table cells + the row's urgent accent.
//   `diligByCase`: Map(caseId → { stage, deadline, quotesSent, quotesOffered }).
// "Diligencia activa" reads through lib/diligenciaStage (the SOT adapter); the
// verification estado survives only as a fallback row tint (no column/filter).
// `ult` (última actuación) has no field in /cases yet — it falls back to "—".
function mapCases(list, diligByCase) {
  return list.map((raw) => {
    const c = raw.case ?? raw;
    const id = c.caseId ?? c.id ?? raw.caseId ?? raw.id ?? buildRol(c);
    const rol = buildRol(c);
    const titulo = titleCase(c.title || c.partes || '') || '—';
    const tribunal = tribunalLabel(c);
    const corte = c.courtName || c.corte || '—';
    const sub = [c.procedure || c.procedimiento, c.competence]
      .filter(Boolean)
      .join(' · ');
    const dilig = diligByCase ? diligByCase.get(id) : null;
    const stage = causaDiligenciaStage(dilig);
    const dilId = dilig?.id;
    const verif = causaVerificationDisplay(c.verificationStatus);
    return {
      id,
      // Raw fields for the (Próx) bulk CSV export — plain strings, not the
      // React-node cells below.
      csv: { rol, caratula: titulo, tribunal, corte },
      // Red row accent when the active diligencia is urgent, or the case itself
      // is in an attention verification state (failed scrape).
      urgent: stage.urgent || verif.dot === 'err',
      cells: {
        rol,
        caratula: (
          <div>
            <div className="tbl__primary">{titulo}</div>
            {sub && <div className="tbl__secondary">{sub}</div>}
          </div>
        ),
        tribunal: (
          <div>
            <div className="tbl__cell-2">{tribunal}</div>
            <div className="tbl__secondary">{corte}</div>
          </div>
        ),
        // Diligencia cell: stage pill + "D-#### · estado" secondary;
        // "Sin diligencia" when active but none exists; "—" when the causa
        // itself is closed (Terminada) — nothing to act on, not a gap.
        diligencia:
          c.legalStatus === 'Terminada' ? (
            <span className="dil-none">—</span>
          ) : stage.stage === null ? (
            <span className="dil-none">Sin diligencia</span>
          ) : (
            <div>
              <StagePill tone={stage.tone} label={stage.label} />
              <div className="tbl__secondary" style={{ marginTop: 4 }}>
                {dilId ? `${dilId} · ` : ''}
                {stage.status}
              </div>
            </div>
          ),
        // Última actuación PJUD — sin campo en /cases todavía (gap de API).
        ult: c.ultActuacion || c.lastActuationLabel || '—',
      },
    };
  });
}

export default function Causas() {
  useDocumentTitle('Causas');
  const navigate = useNavigate();
  const { setBreadcrumb, setPills } = useOutletContext();
  const { organizationId, isInternal } = useAuth();
  const toast = useToast();
  // Bulk select (Próx, gated by exp): { [caseId]: true }.
  const [sel, setSel] = useState({});
  // "Nueva causa" opens the form in the right drawer.
  const [creating, setCreating] = useState(false);
  // The just-created case, so we can navigate straight to its detail page —
  // before the list refetch lands — seeded from the create response.
  const [justCreated, setJustCreated] = useState(null);
  const {
    canAssign,
    cortes,
    tribunalesPorCorte,
    members: encargadoMembers,
    values: createValues,
    setField: setCreateField,
    resetForm: resetCreateForm,
    resetError: resetCreateError,
    handleSubmit: handleCreateCase,
    apiError: createError,
    isSubmitting: creatingSubmitting,
  } = useNuevaCausaForm({
    onCreated: useCallback((created) => {
      setCreating(false);
      setJustCreated(created ?? null);
    }, []),
  });
  const closeCreate = useCallback(() => {
    setCreating(false);
    resetCreateError();
  }, [resetCreateError]);

  // "Importar CSV" (bulk) is Próx → enabled only with experimental features on.
  const [exp] = useExpFeatures();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');

  // The retired /causas/nueva route redirects here with ?nueva=1, so a saved
  // link still opens the modal instead of landing on the bare list. Fires on
  // every searchParams change that carries the flag (also a same-page
  // navigation to /causas/nueva while already on /causas), and
  // setSearchParams strips it right after so it does not re-fire for the
  // same visit.
  useEffect(() => {
    if (searchParams.get('nueva') !== '1') return;
    setCreating(true);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('nueva');
        return params;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);
  // Sort drives the server query; the control is the clickable column headers
  // (SortHead). Default order is createdAt desc.
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortBy, setSortBy] = useState('desc');
  // Server-backed filters: { [queryParam]: value[] }.
  const [filters, setFilters] = useState({});
  // Page-based pagination. pageSize caps at the API's 100.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  // Visible columns (toggled from the toolbar). Persisted on change.
  const [cols, setColsState] = useState(loadCausasCols);
  const setCols = useCallback((v) => {
    setColsState(v);
    try {
      localStorage.setItem(CAUSAS_COLS_KEY, JSON.stringify(v));
    } catch {
      /* private mode / quota — fall back to in-memory only */
    }
  }, []);
  // Lifecycle tabs: Activas, Asignadas, Terminadas. Declared up here with the
  // other state because the page-reset effect below lists `tab` as a dep, and a
  // later `const` would be in the temporal dead zone at first render.
  // The tab lives in ?tab=, which the Inicio "Causas activas" tile sets, so the
  // link opens the tab it names and a reload keeps the one the user picked.
  const TABS = ['activas', 'asignadas', 'terminadas'];
  const tabFromUrl = searchParams.get('tab');
  const tab = TABS.includes(tabFromUrl) ? tabFromUrl : 'activas';
  const setTab = useCallback(
    (next) =>
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('tab', next);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  // Any search / sort / filter / tab change resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortBy, filters, tab]);

  // Tribunal filter options — the directory of tribunals (server list, name-sorted).
  const { data: tribunalsData } = useTribunals({
    enabled: organizationId != null,
  });
  const tribunalOptions = useMemo(
    () =>
      (tribunalsData ? extractList(tribunalsData) : []).map((t) => ({
        value: t.tribunalId ?? t.id,
        label: t.tribunalName ?? t.name,
      })),
    [tribunalsData],
  );

  // Corte filter options — the directory of Cortes de Apelaciones.
  const { data: courtsData } = useCourts({ enabled: organizationId != null });
  const courtOptions = useMemo(
    () =>
      (courtsData ? extractList(courtsData) : []).map((c) => ({
        value: c.courtId ?? c.id,
        label: c.courtName ?? c.name,
      })),
    [courtsData],
  );

  const setFilter = useCallback(
    (key, v) => setFilters((prev) => ({ ...prev, [key]: v })),
    [],
  );
  // Filter groups for the "Filtros" panel. The four server-backed filters
  // (Tribunal/Corte/Competencia/Diligencia), rendered as button + panel + chips.
  const filterGroups = useMemo(
    () => [
      {
        key: 'tribunalId',
        label: 'Tribunal',
        value: filters.tribunalId || [],
        options: tribunalOptions,
        onChange: (v) => setFilter('tribunalId', v),
      },
      {
        key: 'courtId',
        label: 'Corte',
        value: filters.courtId || [],
        options: courtOptions,
        onChange: (v) => setFilter('courtId', v),
      },
      {
        key: 'competence',
        label: 'Competencia',
        value: filters.competence || [],
        options: COMPETENCE_FILTER_OPTIONS,
        onChange: (v) => setFilter('competence', v),
      },
      {
        key: 'diligenciaStatus',
        label: 'Diligencia',
        value: filters.diligenciaStatus || [],
        options: DILIGENCIA_FILTER_OPTIONS,
        onChange: (v) => setFilter('diligenciaStatus', v),
      },
    ],
    [filters, tribunalOptions, courtOptions, setFilter],
  );

  useEffect(() => {
    setBreadcrumb(['Causas']);
    // The page actions live in the PageHead, not the topbar pills.
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);

  // Server-paginated case list (tab → legalStatus/assignee mapping lives in
  // the hook).
  const {
    data: casesData,
    isLoading: casesLoading,
    isError: casesError,
    refetch: refetchCases,
  } = useCasesList({
    organizationId,
    tab,
    page,
    pageSize,
    search: search || undefined,
    sortKey,
    sortBy,
    tribunalId: csv(filters.tribunalId),
    courtId: csv(filters.courtId),
    competence: csv(filters.competence),
    diligenciaStatus: csv(filters.diligenciaStatus),
  });
  const cases = useMemo(
    () => (casesData ? extractList(casesData) : []),
    [casesData],
  );
  const total = casesData ? extractTotal(casesData, cases) : 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Per-case diligencia (active task stage + deadline + quote tallies) — one
  // slim org-scoped query feeding the "Diligencia activa" column.
  const { data: diligData, isLoading: diligLoading } = useActiveDiligencias(
    { organizationId },
    { enabled: organizationId != null },
  );
  const diligByCase = useMemo(() => {
    const m = new Map();
    (diligData?.results ?? []).forEach((d) =>
      m.set(d.caseId, {
        id: d.displayId ?? d.code ?? d.id ?? null,
        stage: d.stage,
        deadline: d.deadline,
        deadlineExpiresAt: d.deadlineExpiresAt,
        // The receptor's committed date: an en-curso diligencia past it reads
        // "Atrasada" and counts as attn, so dropping it undercounts the headline.
        // GET /tasks/active-summary serves the field for this reading.
        dateOffered: d.dateOffered,
        quotesSent: d.quotesSent,
        quotesOffered: d.quotesOffered,
      }),
    );
    return m;
  }, [diligData]);

  // Org-wide attention count for the headline — getActiveDiligencias returns
  // every active diligencia (not just the current page), so this is accurate.
  const attnCount = useMemo(() => {
    let n = 0;
    diligByCase.forEach((d) => {
      if (causaDiligenciaStage(d).attn) n += 1;
    });
    return n;
  }, [diligByCase]);

  const isLoading = organizationId == null || casesLoading || diligLoading;
  // Only the cases query gates the error row: it is the one that produces the
  // rows. The per-causa diligencia summary enriches a column, so its failure
  // leaves the list readable and must not blank it.
  const isError = !isLoading && casesError;

  // After a successful create, navigate straight to the new case's detail page.
  useEffect(() => {
    const id = justCreated && (justCreated.caseId ?? justCreated.id);
    if (id) navigate(`/causas/${id}`);
  }, [justCreated, navigate]);

  // The server filters by tab through active, legalStatus and assignee, so the
  // page it returns is already the tab's set.
  const rows = useMemo(
    () => mapCases(cases, diligByCase),
    [cases, diligByCase],
  );

  // ── Bulk select (Próx) ── checkbox selection over the visible rows, gated by
  // experimental features. Only "Exportar" is real (client-side CSV); "Asignar"
  // and "Archivar" have no backend yet → demo toast.
  const selIds = useMemo(() => Object.keys(sel).filter((k) => sel[k]), [sel]);
  const allChecked = rows.length > 0 && rows.every((r) => sel[r.id]);
  const clearSel = useCallback(() => setSel({}), []);
  // Drop the selection whenever the visible set changes (tab/filters/search/page)
  // or the user opts out of experimental features.
  useEffect(() => {
    setSel({});
  }, [tab, search, filters, page, exp]);

  const exportSel = useCallback(() => {
    // Scoped to the current page's selection (the list is server-paginated and
    // you can only check rendered rows). A whole-result-set export would need a
    // server-side export or fetching all pages — out of scope for this Próx demo.
    const chosen = rows.filter((r) => sel[r.id]);
    if (!chosen.length) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Rol', 'Carátula', 'Tribunal', 'Corte'];
    const lines = [
      header.map(esc).join(','),
      ...chosen.map((r) =>
        [r.csv.rol, r.csv.caratula, r.csv.tribunal, r.csv.corte]
          .map(esc)
          .join(','),
      ),
    ];
    // Prepend a BOM (\ufeff) so Excel reads the UTF-8 accents (á, ñ) correctly.
    // User-initiated download (their click), client-generated — no external I/O.
    const content = `\ufeff${lines.join('\r\n')}`;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `causas-${chosen.length}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, sel]);

  const demoBulk = useCallback(
    (label) =>
      toast({
        kind: 'info',
        message: `${label}: función en desarrollo (Próx). Aún sin backend.`,
        duration: 4000,
      }),
    [toast],
  );

  const causasSelbar =
    exp && selIds.length > 0 ? (
      <div className="selbar">
        <span className="selbar__n">
          <b>{selIds.length}</b> seleccionada{selIds.length > 1 ? 's' : ''}
        </span>
        <div className="selbar__sp" />
        <button
          className="t-btn t-btn--quiet t-btn--sm"
          type="button"
          onClick={exportSel}
        >
          Exportar {selIds.length}
        </button>
        <button
          className="t-btn t-btn--quiet t-btn--sm"
          type="button"
          onClick={() => demoBulk('Asignar encargado')}
        >
          Asignar
          <span className="soon-badge">Próx</span>
        </button>
        <button
          className="t-btn t-btn--quiet t-btn--sm"
          type="button"
          onClick={() => demoBulk('Archivar')}
        >
          Archivar
          <span className="soon-badge">Próx</span>
        </button>
        <button
          className="t-btn t-btn--ghost t-btn--sm"
          type="button"
          onClick={clearSel}
        >
          Limpiar
        </button>
      </div>
    ) : null;

  const pageHead = useMemo(
    () => ({
      kicker: 'Operación',
      title: 'Causas',
      sub: (
        <>
          Causas que el estudio sigue en el Poder Judicial.{' '}
          {attnCount > 0 ? (
            <>
              <b style={{ fontWeight: 600, color: 'var(--ink)' }}>
                {attnCount}
              </b>{' '}
              {attnCount === 1 ? 'requiere' : 'requieren'} acción ahora.
            </>
          ) : (
            'Ninguna requiere acción ahora.'
          )}
        </>
      ),
    }),
    [attnCount],
  );

  const actions = (
    <>
      {/* Próx: se habilita con Funciones experimentales (sin backend aún → demo). */}
      {isInternal && (
        <button
          className="t-btn t-btn--quiet"
          type="button"
          disabled={!exp}
          title="Próximamente: todavía sin backend de importación"
        >
          <Upload size={15} />
          Importar CSV
          <span className="soon-badge">Próx</span>
        </button>
      )}
      <button
        className="t-btn t-btn--primary"
        type="button"
        onClick={() => setCreating(true)}
      >
        <Plus size={15} />
        Suscribir causa
        <span className="kbd">N</span>
      </button>
    </>
  );

  const tabs = {
    active: tab,
    onChange: setTab,
    items: [
      {
        key: 'activas',
        label: 'Activas',
        count: tab === 'activas' ? total : undefined,
      },
      {
        key: 'asignadas',
        label: 'Asignadas',
        count: tab === 'asignadas' ? total : undefined,
      },
      {
        key: 'terminadas',
        label: 'Terminadas',
        count: tab === 'terminadas' ? total : undefined,
      },
    ],
  };

  return (
    <>
      <CausasLista
        tabs={tabs}
        pageHead={pageHead}
        actions={actions}
        rows={rows}
        selection={
          exp
            ? {
                enabled: true,
                isSelected: (id) => !!sel[id],
                onToggle: (id) => setSel((s) => ({ ...s, [id]: !s[id] })),
                onToggleAll: () =>
                  setSel(
                    allChecked
                      ? {}
                      : Object.fromEntries(rows.map((r) => [r.id, true])),
                  ),
                allChecked,
                bar: causasSelbar,
              }
            : undefined
        }
        isLoading={isLoading}
        isError={isError}
        onRetry={refetchCases}
        onRowClick={(row) => {
          if (creating) return;
          navigate(`/causas/${row.id}`);
        }}
        searchValue={search}
        onSearchChange={setSearch}
        filterGroups={filterGroups}
        columns={{
          value: cols,
          onChange: setCols,
          opts: CAUSAS_COLS,
          lockedNote: 'Rol y carátula van siempre.',
        }}
        sortKey={sortKey}
        sortBy={sortBy}
        onSortChange={(key, dir) => {
          setSortKey(key);
          setSortBy(dir);
        }}
        pager={{
          page,
          pageCount,
          total,
          pageSize,
          onPage: setPage,
          onPageSize: (n) => {
            setPageSize(n);
            setPage(1);
          },
        }}
      />
      <SuscribirCausaModal
        open={creating}
        onClose={closeCreate}
        cortes={cortes}
        tribunalesPorCorte={tribunalesPorCorte}
        members={encargadoMembers}
        canAssign={canAssign}
        values={createValues}
        onChange={setCreateField}
        onClear={resetCreateForm}
        onSubmit={handleCreateCase}
        apiError={createError}
        isSubmitting={creatingSubmitting}
      />
    </>
  );
}
